// Oylan stand: the homework journal shared between the child's browser and the parent's site
// (oylan/spec_zhurnal_roditelya.md).
//
// The child's browser keeps the journal in localStorage as before and, after every answer, sends
// the whole journal to /api/journal (a Cloudflare Pages Function, cloudflare/stend). The parent's
// site oylan-roditel.pages.dev reads it back from there as ready table rows.

import { homeworkLabel, summary } from "./homework";

export const JOURNAL_URL = "/api/journal";
export const PARENT_ORIGIN = "https://oylan-roditel.pages.dev";
// Not a secret: it is in the site's code. It only turns away requests that did not come from the stand.
export const WRITE_KEY = "oylan-stend-journal-v1";
export const MAX_CHARS = 200000;  // the journal is mostly digits and Latin keys, so about 200 KB

const STATUS = { new: "не начата", done: "сдана сама", steps: "решает по шагам", review: "на повторе", learned: "освоена" };

function dayLabel(day) {
    const [, m, d] = day.split("-");
    return `${d}.${m}`;
}

// The parent's table, one block per lesson; the same columns as #/roditel.
export function parentRows(homework, journal) {
    return homework.map((lesson) => ({
        label: homeworkLabel(lesson),
        rows: lesson.tasks.map((t) => {
            const s = summary(t, journal[t.id]);
            return {
                task: `${t.id} ${t.title}`,
                status: STATUS[s.status] || s.status,
                first: s.firstOwn ? `${s.firstOwn.input} ${s.firstOwn.ok ? "✓" : "✗"}` : "",
                tries: s.tries || "",
                shown: s.shown || "",
                weak: s.weakSteps.map((w) => `«${w.q}» ${w.times} раза`).join("; "),
                due: s.status === "review" && s.due ? dayLabel(s.due) : "",
            };
        }),
    }));
}

const cors = { "Access-Control-Allow-Origin": PARENT_ORIGIN, "Content-Type": "application/json; charset=utf-8" };

// The function's logic without Cloudflare: req = {method, key, text}, store = {get(), put(text)}.
// The newer journal wins: a write older than the stored one (another device) is refused.
export async function handleJournal(req, store, homework) {
    const reply = (status, body) => ({ status, headers: cors, body: JSON.stringify(body) });
    if (req.method === "GET") {
        const saved = JSON.parse((await store.get()) || "null");
        if (!saved) return reply(200, { savedAt: null, lessons: parentRows(homework, {}) });
        return reply(200, { savedAt: saved.savedAt, lessons: parentRows(homework, saved.journal) });
    }
    if (req.method !== "PUT") return reply(405, { error: "only GET and PUT" });
    if (req.key !== WRITE_KEY) return reply(403, { error: "wrong key" });
    if ((req.text || "").length > MAX_CHARS) return reply(413, { error: "too big" });
    let body;
    try {
        body = JSON.parse(req.text);
    } catch (e) {
        return reply(400, { error: "not JSON" });
    }
    if (!body || typeof body.journal !== "object" || Array.isArray(body.journal) || typeof body.savedAt !== "string") {
        return reply(400, { error: "need {journal, savedAt}" });
    }
    const old = JSON.parse((await store.get()) || "null");
    if (old && old.savedAt > body.savedAt) return reply(409, { error: "a newer journal is stored", savedAt: old.savedAt });
    await store.put(JSON.stringify({ journal: body.journal, savedAt: body.savedAt }));
    return reply(200, { savedAt: body.savedAt });
}

// The child's side: send the whole journal. Offline or a failed request changes nothing in the
// browser; the next answer or the next visit of the homework page sends the journal again.
export async function pushJournal(journal, { fetchFn = fetch, now = new Date() } = {}) {
    try {
        const res = await fetchFn(JOURNAL_URL, {
            method: "PUT",
            headers: { "Content-Type": "application/json", "X-Oylan-Key": WRITE_KEY },
            body: JSON.stringify({ journal, savedAt: now.toISOString() }),
            keepalive: true,
        });
        return res.ok;
    } catch (e) {
        return false;
    }
}
